import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Upload, Send, ArrowLeft, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function VCRStage3({ report, onNext, onPrevious, onSave }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (report.stage3_photos) {
      setPhotos(report.stage3_photos);
    }
  }, [report]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          return null;
        }

        const reader = new FileReader();
        return new Promise((resolve) => {
          reader.onload = (e) => {
            resolve({
              filename: file.name,
              data: e.target.result.split(',')[1],
              file_type: file.type,
              uploaded_at: new Date().toISOString()
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const newPhotos = (await Promise.all(uploadPromises)).filter(p => p !== null);
      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);

      // Auto-save to backend
      await axios.put(
        `${API}/vcr/${report.id}/stage3`,
        { photos: updatedPhotos },
        getAuthHeaders()
      );
      
      toast.success(`${newPhotos.length} photo(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (index) => {
    const updated = photos.filter((_, i) => i !== index);
    setPhotos(updated);
    
    try {
      await axios.put(
        `${API}/vcr/${report.id}/stage3`,
        { photos: updated },
        getAuthHeaders()
      );
      toast.success('Photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const handleSubmit = async () => {
    if (photos.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }

    try {
      await axios.post(
        `${API}/vcr/${report.id}/submit-stage3`,
        {},
        getAuthHeaders()
      );
      toast.success('Stage 3 submitted');
      if (onNext) onNext();
    } catch (error) {
      toast.error('Failed to submit stage 3');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-blue-300 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-blue-600">Stage 3: Incident Photos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Upload up to 10 photos documenting the incident, vehicle damage, and scene.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={`data:${photo.file_type};base64,${photo.data}`}
                  alt={photo.filename}
                  className="w-full h-32 object-cover rounded-lg border-2 border-blue-200 dark:border-slate-700"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 truncate">{photo.filename}</p>
              </div>
            ))}

            {photos.length < 10 && (
              <label className="border-2 border-dashed border-slate-300 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors dark:border-slate-700">
                <Upload className="h-8 w-8 text-slate-400 mb-2" />
                <span className="text-xs text-slate-600 dark:text-slate-400">Upload Photo</span>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          <p className="text-sm text-slate-500 dark:text-slate-400">
            {photos.length} / 10 photos uploaded
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onPrevious}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Stage 2
        </Button>
        <Button type="button" onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
          <Send className="h-4 w-4 mr-2" />
          Submit Photos & Continue
        </Button>
      </div>
    </div>
  );
}
